import React, { useState, useMemo, useEffect } from 'react';
import { Header } from '@appkit4/react-components/header';
import { Link, useNavigate, useLocation, matchPath } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import AvatarDropdown from './AvatarDropdown';
import { AppRoutes } from '../../Constants';
import useLocale from '../../locale/useLocale';
import { useTenant } from '../../context/TenantContext';
import { useEngagement } from '../../context/EngagementContext';
import { useProject } from '../../context/ProjectContext';
import {
    useCreateTimePeriod,
    useActivateTimePeriod,
    useCreateEntity,
    useLinkEntityToTimePeriod,
    useGetEntitiesNotLinkedToTimePeriod,
    useGetJurisdictions,
    useDeleteEntity,
    useGetFAProgressBarStatus,
    useGetProjectProgressBarStatus,
    getGetTimePeriodsQueryKey,
    getGetEntitiesForTimePeriodQueryKey,
    getGetEntitiesNotLinkedToTimePeriodQueryKey,
} from '../../api/generated/endpoint';
import { useQueryClient } from '@tanstack/react-query';
import DropDown from '../reusables/DropDown';
import StackedProgressBar from '../reusables/StackedProgressBar';
import { ReactComponent as PwCLogo } from '../../assets/icons/New Brand Icons/PwC Logo.svg';
import { Select } from '@appkit4/react-components';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { Input } from '@appkit4/react-components/field';
import { Checkbox } from '@appkit4/react-components/checkbox';
import { CalendarPicker } from '@appkit4/react-components/datepicker';
import useToast from '../../helpers/useToast';
import useGenericErrorToast from '../../helpers/useGenericErrorToast';
import './Navigation.scss';

const Navigation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    const { l } = useLocale();
    const queryClient = useQueryClient();
    const showToast = useToast();
    const showGenericErrorToast = useGenericErrorToast();
    const { selectedTenant, clearTenant } = useTenant();
    const {
        activeTimeId,
        selectedEntityId,
        setSelectedEntityId,
        timePeriods,
        entities,
    } = useEngagement();

    const isEngagementRoute = [
        AppRoutes.FixedAsset,
        AppRoutes.ProjectData,
        AppRoutes.TaxPools,
        AppRoutes.Rules,
        AppRoutes.Reporting,
        AppRoutes.Log,
    ].some((route) =>
        matchPath({ path: route, end: false }, location.pathname)
    );

    // Clear tenant when navigating to Home page
    useEffect(() => {
        if (location.pathname === '/') {
            clearTenant();
        }
    }, [location.pathname, clearTenant]);

    // Version info from seeded mock data in QueryClient
    const versionInfo = {
        versionTag: '0.0.1-dev',
        branchName: 'local',
        commitId: 'mock',
    };
    const isLoadingVersionInfo = false;

    // Secondary bar dropdown state - derive period value from context's activeTimeId
    const selectedPeriod =
        activeTimeId !== undefined ? String(activeTimeId) : undefined;

    // Add Entity modal state
    const [showAddEntity, setShowAddEntity] = useState(false);
    const [showLinkEntity, setShowLinkEntity] = useState(false);
    const [pendingModal, setPendingModal] = useState<'entity' | 'link' | null>(
        null
    );
    const [newEntityCompanyId, setNewEntityCompanyId] = useState('');
    const [newEntityName, setNewEntityName] = useState('');
    const [newEntityJurisdiction, setNewEntityJurisdiction] = useState<
        string | undefined
    >();
    const [newEntityRolloverRules, setNewEntityRolloverRules] = useState(false);

    // Add Period modal state
    const [showAddPeriod, setShowAddPeriod] = useState(false);
    const [newPeriodName, setNewPeriodName] = useState('');
    const [newPeriodDateRange, setNewPeriodDateRange] = useState<any>(null);

    // Delete Entity confirmation state
    const [entityToDelete, setEntityToDelete] = useState<{
        id: number;
        name: string;
    } | null>(null);

    // Progress bar expand/collapse
    const [progressExpanded, setProgressExpanded] = useState(false);

    // Open queued modal after the previous one has fully closed
    useEffect(() => {
        if (pendingModal === 'entity' && !showLinkEntity && !showAddPeriod) {
            setPendingModal(null);
            setNewEntityCompanyId('');
            setNewEntityName('');
            setNewEntityJurisdiction('');
            setNewEntityRolloverRules(false);
            setShowAddEntity(true);
        }
        if (pendingModal === 'link' && !showAddPeriod && !showAddEntity) {
            setPendingModal(null);
            setShowLinkEntity(true);
        }
    }, [pendingModal, showLinkEntity, showAddPeriod, showAddEntity]);

    // Build dropdown options from real data — active period is first in the list
    const periodOptions = useMemo(() => {
        const sorted = [...(timePeriods ?? [])].sort((a, b) =>
            a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1
        );
        const options = sorted.map((p) => ({
            value: String(p.timeId),
            label: p.shortName ?? '',
        }));
        options.push({ value: '__add_period', label: '+ Add Period' });
        return options;
    }, [timePeriods]);

    const entityOptions = useMemo(() => {
        const options = (entities ?? []).map((e) => ({
            value: String(e.entityId),
            label: e.entityDisplayName ?? '',
        }));
        options.push({
            value: '__create_entity',
            label: '+ Create New Entity',
        });
        options.push({
            value: '__link_entity',
            label: '+ Link Existing Entity',
        });
        return options;
    }, [entities]);

    const handleDeleteEntityClick = (
        e: React.MouseEvent,
        entityId: number,
        entityName: string
    ) => {
        e.stopPropagation();
        setEntityToDelete({ id: entityId, name: entityName });
    };

    const handleConfirmDelete = () => {
        if (entityToDelete) {
            deleteEntity({ entityId: entityToDelete.id });
        }
    };

    const { data: jurisdictions } = useGetJurisdictions({
        query: { enabled: isEngagementRoute && showAddEntity },
    });
    const jurisdictionOptions = (jurisdictions ?? []).map((j) => ({
        value: String(j.jurisdictionId),
        label: j.countryName ?? '',
    }));

    // Mutations
    const { mutate: createTimePeriod } = useCreateTimePeriod({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: getGetTimePeriodsQueryKey(),
                });
                setShowAddPeriod(false);
                setNewPeriodName('');
                setNewPeriodDateRange(null);
                setPendingModal('link');
                showToast(
                    'Success',
                    'Time period created successfully',
                    'success'
                );
            },
            onError: (error) => {
                showGenericErrorToast(error);
            },
        },
    });

    const { mutate: activateTimePeriod } = useActivateTimePeriod({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: getGetTimePeriodsQueryKey(),
                });
            },
            onError: (error) => {
                showGenericErrorToast(error);
            },
        },
    });

    const { mutate: createEntity } = useCreateEntity({
        mutation: {
            onSuccess: () => {
                if (activeTimeId) {
                    queryClient.invalidateQueries({
                        queryKey:
                            getGetEntitiesForTimePeriodQueryKey(activeTimeId),
                    });
                }
                setShowAddEntity(false);
                setNewEntityCompanyId('');
                setNewEntityName('');
                setNewEntityJurisdiction('');
                setNewEntityRolloverRules(false);
                showToast('Success', 'Entity created successfully', 'success');
            },
            onError: (error) => {
                showGenericErrorToast(error);
            },
        },
    });

    const { mutate: linkEntity } = useLinkEntityToTimePeriod({
        mutation: {
            onSuccess: () => {
                if (activeTimeId) {
                    queryClient.invalidateQueries({
                        queryKey:
                            getGetEntitiesForTimePeriodQueryKey(activeTimeId),
                    });
                    queryClient.invalidateQueries({
                        queryKey:
                            getGetEntitiesNotLinkedToTimePeriodQueryKey(
                                activeTimeId
                            ),
                    });
                }
                showToast('Success', 'Entity linked successfully', 'success');
                setShowLinkEntity(false);
            },
            onError: (error) => {
                showGenericErrorToast(error);
            },
        },
    });

    const { mutate: deleteEntity } = useDeleteEntity({
        mutation: {
            onSuccess: () => {
                if (activeTimeId) {
                    queryClient.invalidateQueries({
                        queryKey:
                            getGetEntitiesForTimePeriodQueryKey(activeTimeId),
                    });
                    queryClient.invalidateQueries({
                        queryKey:
                            getGetEntitiesNotLinkedToTimePeriodQueryKey(
                                activeTimeId
                            ),
                    });
                }
                showToast('Success', 'Entity deleted successfully', 'success');

                // If no other entities remain, prompt user to add/link one
                const remaining = (entities ?? []).filter(
                    (e) => e.entityId !== entityToDelete?.id
                );
                if (remaining.length === 0) {
                    setShowLinkEntity(true);
                } else {
                    setSelectedEntityId(remaining[0].entityId!);
                }

                setEntityToDelete(null);
            },
            onError: (error) => {
                showGenericErrorToast(error);
                setEntityToDelete(null);
            },
        },
    });

    const { data: unlinkedEntities } = useGetEntitiesNotLinkedToTimePeriod(
        activeTimeId ?? 0,
        { query: { enabled: !!activeTimeId && showLinkEntity } }
    );

    const handleEntitySelect = (value: string | string[]) => {
        const val = Array.isArray(value) ? value[0] : value;
        if (val === '__create_entity') {
            setNewEntityCompanyId('');
            setNewEntityName('');
            setNewEntityJurisdiction('');
            setNewEntityRolloverRules(false);
            setShowAddEntity(true);
        } else if (val === '__link_entity') {
            setShowLinkEntity(true);
        } else {
            setSelectedEntityId(parseInt(val, 10));
        }
    };

    const handlePeriodSelect = (value: string | string[]) => {
        const val = Array.isArray(value) ? value[0] : value;
        if (val === '__add_period') {
            setShowAddPeriod(true);
        } else {
            activateTimePeriod({ timeId: parseInt(val, 10) });
        }
    };

    const handleAddEntity = () => {
        if (
            !newEntityName ||
            !newEntityCompanyId ||
            !newEntityJurisdiction ||
            !activeTimeId
        )
            return;
        createEntity({
            data: {
                timeId: activeTimeId,
                entityDisplayName: newEntityName,
                companyId: newEntityCompanyId,
                jurisdictionId: parseInt(newEntityJurisdiction, 10),
                rollover: newEntityRolloverRules,
            },
        });
    };

    const formatDate = (d: Date): string => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleAddPeriod = () => {
        if (!newPeriodName || !newPeriodDateRange) return;

        let start: string;
        let end: string;

        if (Array.isArray(newPeriodDateRange)) {
            if (newPeriodDateRange.length !== 2) return;
            start = formatDate(new Date(newPeriodDateRange[0]));
            end = formatDate(new Date(newPeriodDateRange[1]));
        } else if (typeof newPeriodDateRange === 'string') {
            const dates = newPeriodDateRange.split(' - ');
            if (dates.length !== 2) return;
            start = dates[0];
            end = dates[1];
        } else {
            return;
        }

        createTimePeriod({
            data: {
                shortName: newPeriodName,
                start,
                end,
            },
        });
    };

    const { activeProjectId, activeVersion } = useProject();

    const isOnFARoute = !!matchPath(
        { path: AppRoutes.FixedAsset, end: false },
        location.pathname
    );
    const isOnProjectRoute = !!matchPath(
        { path: AppRoutes.ProjectData, end: false },
        location.pathname
    );

    // Sticky mode: only changes when navigating to FA or Project routes.
    // On other routes (Pools, Rules, etc.) keeps showing the last loaded data.
    const [progressBarMode, setProgressBarMode] = useState<'fa' | 'project'>(
        'fa'
    );

    useEffect(() => {
        if (isOnFARoute) {
            setProgressBarMode('fa');
        } else if (isOnProjectRoute) {
            setProgressBarMode('project');
        }
    }, [isOnFARoute, isOnProjectRoute]);

    const showProjectBars = progressBarMode === 'project';

    const { data: faProgressData } = useGetFAProgressBarStatus(
        { entityId: selectedEntityId, timeId: activeTimeId },
        {
            query: {
                enabled:
                    isOnFARoute &&
                    selectedEntityId !== undefined &&
                    activeTimeId !== undefined,
            },
        }
    );

    const { data: projectProgressData } = useGetProjectProgressBarStatus(
        {
            entityId: selectedEntityId,
            timeId: activeTimeId,
            projectId: activeProjectId,
            version: activeVersion,
        },
        {
            query: {
                enabled:
                    isOnProjectRoute &&
                    selectedEntityId !== undefined &&
                    activeTimeId !== undefined &&
                    activeProjectId !== undefined &&
                    activeVersion !== undefined,
            },
        }
    );

    const progressData = showProjectBars ? projectProgressData : faProgressData;

    const matchedRowsPct = progressData?.matchedRowsPercent ?? 0;
    const matchedRows = progressData?.matchedRows ?? 0;
    const valuePct = progressData?.valueAnalysedPercent ?? 0;

    const formatCurrency = (val: number) =>
        val.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });

    const ruleAnalysisSegments = [
        {
            label: 'Default Rules',
            value: progressData?.defaultRulesPercent ?? 0,
            color: '#d93954',
        },
        {
            label: 'Additional Rules',
            value: progressData?.additionalRulesPercent ?? 0,
            color: '#e8637a',
        },
        {
            label: 'Manual',
            value: progressData?.manualPercent ?? 0,
            color: '#f0a0b0',
        },
        { label: 'AI', value: progressData?.aiPercent ?? 0, color: '#f5c8d0' },
    ].filter((s) => s.value > 0);

    const rowsSegments = [
        {
            label: 'Rows analysed',
            value: matchedRowsPct,
            color:
                matchedRowsPct > 75
                    ? '#175d2d'
                    : matchedRowsPct >= 50
                      ? '#ffb600'
                      : '#E0301E',
        },
    ];
    const rowsTooltip = (
        <span>
            {matchedRows.toLocaleString()} /{' '}
            {(progressData?.totalRows ?? 0).toLocaleString()} rows
        </span>
    );

    const valueSegments = [
        {
            label: 'Value analysed',
            value: valuePct,
            color:
                valuePct > 75
                    ? '#175d2d'
                    : valuePct >= 50
                      ? '#ffb600'
                      : '#E0301E',
        },
    ];
    const valueTooltip = progressData?.valueTooltip ? (
        <span>{progressData.valueTooltip}</span>
    ) : (
        <span>No value analysed</span>
    );

    // Decide where the secondary bar should appear
    const SHOW_LAYOUT = [
        AppRoutes.FixedAsset,
        AppRoutes.ProjectData,
        AppRoutes.TaxPools,
        AppRoutes.Rules,
        AppRoutes.Reporting,
        AppRoutes.Log,
    ];

    const showLayout = SHOW_LAYOUT.some((pattern) =>
        matchPath({ path: pattern, end: false }, location.pathname)
    );

    const titleTemplate = () => (
        <Link
            to={
                auth.isAuthenticated
                    ? AppRoutes.HomePage
                    : AppRoutes.LandingPage
            }
            style={{
                fontWeight: 'normal',
                textDecorationLine: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
            }}
        >
            <PwCLogo style={{ height: '24px', width: 'auto' }} />
            {l('_applicationName')}
        </Link>
    );

    const optionsTemplate = () => {
        let versionLabel = '';
        if (versionInfo?.versionTag) {
            versionLabel = `${l('_version')} : ${versionInfo.versionTag}`;
        } else if (versionInfo?.branchName && versionInfo?.commitId) {
            versionLabel = `${l('_version')} : ${versionInfo?.branchName} - ${
                versionInfo?.commitId
            }`;
        }

        return (
            <>
                {!isLoadingVersionInfo && versionInfo && (
                    <p
                        id="version-text"
                        className="ap-typography-body-xs pt-3 me-3"
                    >
                        {versionLabel}
                    </p>
                )}
            </>
        );
    };

    const userTemplate = () =>
        auth.isAuthenticated ? <AvatarDropdown /> : null;

    const handleLogoNavigation = () => {
        if (auth.isAuthenticated) {
            clearTenant();
            navigate(AppRoutes.HomePage);
        } else {
            navigate(AppRoutes.LandingPage);
        }
    };

    const headerStyle = {
        top: '0',
    };

    return (
        <div className="global-nav">
            <Header
                role="navigation"
                type="solid"
                titleTemplate={titleTemplate}
                optionsTemplate={optionsTemplate}
                userTemplate={userTemplate}
                onClickLogo={handleLogoNavigation}
                className="position-sticky"
                style={headerStyle}
            />

            {showLayout && (
                <div className="nav-secondary-bar flex-column flex-lg-row">
                    <div className="nav-context-selectors d-flex flex-grow-1 gap-3">
                        <Select
                            className="entity-dropdown"
                            data={entityOptions}
                            placeholder="Entity"
                            value={
                                selectedEntityId !== undefined
                                    ? String(selectedEntityId)
                                    : undefined
                            }
                            onSelect={(val: any) => {
                                const v =
                                    typeof val === 'string' ? val : val?.value;
                                handleEntitySelect(v);
                            }}
                            suffixTemplate={(item: any) => {
                                if (
                                    item.value === '__create_entity' ||
                                    item.value === '__link_entity'
                                )
                                    return null;
                                return (
                                    <span
                                        className="entity-delete-icon Appkit4-icon icon-delete-outline"
                                        style={{
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            color: 'var(--color-text-error)',
                                        }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleDeleteEntityClick(
                                                e as unknown as React.MouseEvent,
                                                Number(item.value),
                                                item.label
                                            );
                                        }}
                                    />
                                );
                            }}
                        />
                        <Select
                            className="period-dropdown"
                            data={periodOptions}
                            placeholder="Period"
                            value={selectedPeriod}
                            onSelect={(value: any) => {
                                const selectedValue =
                                    typeof value === 'string'
                                        ? value
                                        : value?.value;
                                handlePeriodSelect(selectedValue);
                            }}
                        />
                    </div>

                    <div className="nav-progress-container d-flex flex-grow-1 align-items-center">
                        <div className="nav-progress-group">
                            <div className="nav-progress-row">
                                <StackedProgressBar
                                    label="Value analysed:"
                                    segments={valueSegments}
                                    size="sm"
                                    tooltipContent={valueTooltip}
                                />
                                <StackedProgressBar
                                    label="Rows analysed:"
                                    segments={rowsSegments}
                                    size="sm"
                                    tooltipContent={rowsTooltip}
                                />
                            </div>
                            {progressExpanded && (
                                <StackedProgressBar
                                    label={
                                        showProjectBars
                                            ? 'Project rule analysis:'
                                            : 'Fixed asset rule analysis:'
                                    }
                                    segments={ruleAnalysisSegments}
                                    size="sm"
                                    showLegend
                                />
                            )}
                        </div>
                        <span
                            className={`nav-progress-toggle Appkit4-icon ${progressExpanded ? 'icon-up-chevron-outline' : 'icon-down-chevron-outline'}`}
                            onClick={() =>
                                setProgressExpanded(!progressExpanded)
                            }
                        />
                    </div>
                </div>
            )}

            {showAddPeriod && (
                <Modal
                    visible={showAddPeriod}
                    title="Add Period"
                    onCancel={() => {
                        setShowAddPeriod(false);
                    }}
                    modalStyle={{ width: '450px' }}
                    footer={
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button
                                kind="primary"
                                onClick={handleAddPeriod}
                                disabled={!newPeriodName || !newPeriodDateRange}
                            >
                                Add period
                            </Button>
                        </div>
                    }
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                        }}
                    >
                        <div>
                            <label
                                style={{
                                    fontWeight: 600,
                                    marginBottom: '4px',
                                    display: 'block',
                                }}
                            >
                                Period Name
                            </label>
                            <Input
                                type="text"
                                required
                                value={newPeriodName}
                                onChange={(value: string) =>
                                    setNewPeriodName(value)
                                }
                                placeholder="Period Name"
                            />
                        </div>
                        <div>
                            <label
                                style={{
                                    fontWeight: 600,
                                    marginBottom: '4px',
                                    display: 'block',
                                }}
                            >
                                Date Range
                            </label>
                            <CalendarPicker
                                placeholder="Date Range"
                                required
                                selectRange
                                onChange={(value: any) =>
                                    setNewPeriodDateRange(value)
                                }
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {showAddEntity && (
                <Modal
                    visible={showAddEntity}
                    title="Add Entity"
                    onCancel={() => setShowAddEntity(false)}
                    modalStyle={{ width: '500px' }}
                    footer={
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button
                                kind="primary"
                                onClick={handleAddEntity}
                                disabled={
                                    !newEntityCompanyId ||
                                    !newEntityName ||
                                    !newEntityJurisdiction
                                }
                            >
                                Add entity
                            </Button>
                        </div>
                    }
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                        }}
                    >
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <div style={{ flex: '0 0 120px' }}>
                                <label
                                    style={{
                                        fontWeight: 600,
                                        marginBottom: '4px',
                                        display: 'block',
                                    }}
                                >
                                    Company ID
                                </label>
                                <Input
                                    type="text"
                                    required
                                    value={newEntityCompanyId}
                                    onChange={(value: string) =>
                                        setNewEntityCompanyId(value)
                                    }
                                    placeholder="Company ID"
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        fontWeight: 600,
                                        marginBottom: '4px',
                                        display: 'block',
                                    }}
                                >
                                    Entity Name
                                </label>
                                <Input
                                    type="text"
                                    required
                                    value={newEntityName}
                                    onChange={(value: string) =>
                                        setNewEntityName(value)
                                    }
                                    placeholder="Entity Name"
                                />
                            </div>
                        </div>
                        <div>
                            <DropDown
                                data={jurisdictionOptions}
                                placeholder="Tax Jurisdiction"
                                value={newEntityJurisdiction}
                                onSelect={(value) =>
                                    setNewEntityJurisdiction(value as string)
                                }
                            />
                        </div>
                        <div>
                            <Checkbox
                                checked={newEntityRolloverRules}
                                onChange={(value: boolean) =>
                                    setNewEntityRolloverRules(value)
                                }
                            >
                                Rollover rules
                            </Checkbox>
                        </div>
                    </div>
                </Modal>
            )}

            {showLinkEntity && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'fixed',
                        inset: 0,
                        zIndex: 10,
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                        }}
                        onClick={() => {
                            if (!entities || entities.length === 0) {
                                showToast(
                                    'Warning',
                                    'There must be at least one entity for the time period',
                                    'warning'
                                );
                                return;
                            }
                            setShowLinkEntity(false);
                        }}
                    />
                    <div
                        role="dialog"
                        aria-label="Link Existing Entity"
                        style={{
                            position: 'relative',
                            width: '500px',
                            backgroundColor:
                                'var(--color-background-default, #fff)',
                            borderRadius: '0.5rem',
                            padding: '1.5rem',
                            zIndex: 11,
                        }}
                    >
                        <h2
                            style={{
                                margin: '0 0 1rem 0',
                                fontSize: '1.25rem',
                            }}
                        >
                            Link Existing Entity
                        </h2>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                            }}
                        >
                            {(unlinkedEntities ?? []).length === 0 ? (
                                <p>No existing entities available to link.</p>
                            ) : (
                                (unlinkedEntities ?? []).map((entity) => (
                                    <div
                                        key={entity.entityId}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '8px 12px',
                                            border: '1px solid var(--color-background-border)',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <div>
                                            <strong>
                                                {entity.entityDisplayName}
                                            </strong>
                                            <span
                                                style={{
                                                    marginLeft: '8px',
                                                    opacity: 0.7,
                                                }}
                                            >
                                                {entity.countryName}
                                            </span>
                                        </div>
                                        <Button
                                            kind="primary"
                                            compact
                                            onClick={() => {
                                                if (
                                                    activeTimeId &&
                                                    entity.entityId
                                                ) {
                                                    linkEntity({
                                                        data: {
                                                            entityId:
                                                                entity.entityId,
                                                            timeId: activeTimeId,
                                                        },
                                                    });
                                                }
                                            }}
                                        >
                                            Link
                                        </Button>
                                    </div>
                                ))
                            )}
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    marginTop: '12px',
                                }}
                            >
                                <Button
                                    kind="secondary"
                                    onClick={() => {
                                        setShowLinkEntity(false);
                                        setPendingModal('entity');
                                    }}
                                >
                                    + Create New Entity
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!!entityToDelete && (
                <Modal
                    visible={!!entityToDelete}
                    title="Delete Entity"
                    onCancel={() => setEntityToDelete(null)}
                    modalStyle={{ width: '400px' }}
                    footer={
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '8px',
                            }}
                        >
                            <Button
                                kind="secondary"
                                onClick={() => setEntityToDelete(null)}
                            >
                                Cancel
                            </Button>
                            <Button
                                kind="negative"
                                onClick={handleConfirmDelete}
                            >
                                Delete
                            </Button>
                        </div>
                    }
                >
                    <p>
                        Are you sure you want to delete entity{' '}
                        <strong>{entityToDelete?.name}</strong>? This will
                        permanently remove all data associated with this entity.
                    </p>
                </Modal>
            )}
        </div>
    );
};

export default Navigation;
